import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks/index'
import { TFunction } from 'i18next'
import styled, { useTheme } from 'particles/themes/styled'
import { summaryInnerCardButtonsStyle, SummaryCardContainer, TaskBlockContainer } from './style'
import {
    setCampaignSubmissionStatus,
    TaskSummary,
    CAMPAIGN_STATUS_ENUM,
    CAMPAIGN_SUBMISSION_STATUS_ENUM,
} from 'state/campaigns'
import { TASK_VIEW_ENUM, SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { buildCloudinaryUrl } from 'services/cloudinary'
import ButtonList from './ButtonList'
import { REGISTRATION_VIEW_ENUM } from 'state/review'
import { usePermission } from 'hooks/permissions'
import { CampaignManagement } from 'components/Shared/Permission/enums'
import Text from 'particles/Text'
import CustomIcon from 'components/Icon/Icon'
import IconButton from 'components/Shared/Button/IconButton'
import { daysToDate } from 'services/dates'
import { SubmissionSummaryState } from 'state/campaigns'
import Progress from './Progress'
import Icon from 'particles/Icon'
import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { ClickAwayListener } from '@mui/material'
import { getTeamTypeOptions } from 'components/Team/Teams/utils'
import { selectIsInternalUser } from 'state/profile'
import { TeamTypeTag } from 'components/TeamFilter'

export const getSubmissionStatusOptions = (newT: TFunction) => {
    return [
        { value: CAMPAIGN_SUBMISSION_STATUS_ENUM.OPEN, label: newT('statusLabel.submissionOpen', 'Submissions Open') },
        {
            value: CAMPAIGN_SUBMISSION_STATUS_ENUM.FILLING_FAST,
            label: newT('statusLabel.submissionFillingFast', 'Submissions Filling Fast'),
        },
        { value: CAMPAIGN_SUBMISSION_STATUS_ENUM.FULL, label: newT('statusLabel.submissionFull', 'Submissions Full') },
    ]
}

interface TaskBlockProps {
    campaignUuid: string
    task: TaskSummary
    viewType?: TASK_VIEW_ENUM
    pendingCount?: number
    isCompleted?: boolean
}

const TaskBlock: React.FC<TaskBlockProps> = ({
    campaignUuid,
    task,
    viewType = TASK_VIEW_ENUM.TASKS,
    pendingCount = 0,
    isCompleted,
}) => {
    const theme = useTheme()
    const [newT] = useNewTranslation()
    const bubbleCount =
        viewType !== TASK_VIEW_ENUM.BATCH_SUBMISSIONS &&
        (viewType == TASK_VIEW_ENUM.REGISTRATION ? pendingCount : task.total_eligible_pending_submissions_count)

    const url =
        viewType == TASK_VIEW_ENUM.BATCH_SUBMISSIONS
            ? `/submissions/review?campaign=${campaignUuid}&task=${task.uuid}&viewType=${SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS}`
            : viewType == TASK_VIEW_ENUM.REGISTRATION
              ? `/submissions/review?campaign=${campaignUuid}&task=${task.uuid}&viewType=${REGISTRATION_VIEW_ENUM.PENDING}`
              : `/submissions/review?campaign=${campaignUuid}&task=${task.uuid}&viewType=${task?.redirect_step?.review_status || SUBMISSION_VIEW_ENUM.PENDING}&step=${task?.redirect_step?.uuid}`
    const title =
        viewType == TASK_VIEW_ENUM.BATCH_SUBMISSIONS
            ? newT('card.submission.allSubmission', 'See All Submissions')
            : viewType == TASK_VIEW_ENUM.REGISTRATION
              ? newT('taskType.registration', 'Registration')
              : task.title

    return (
        <TaskBlockContainer>
            {bubbleCount && bubbleCount > 0 ? (
                <div className={`${isCompleted ? 'bubble-complete' : ''} bubble-container`}>
                    <div className="bubble-text">{bubbleCount}</div>
                </div>
            ) : null}
            <a href={url + '&entry=submission_list'}>
                <div className="task-image-contaner">
                    {isCompleted ? (
                        <CustomIcon.CheckCircleIcon />
                    ) : viewType == TASK_VIEW_ENUM.BATCH_SUBMISSIONS ? (
                        <CustomIcon.AllSubmissionsIcon />
                    ) : viewType == TASK_VIEW_ENUM.REGISTRATION ? (
                        isCompleted ? (
                            <CustomIcon.CheckCircleIcon />
                        ) : (
                            <CustomIcon.RegistrationIcon />
                        )
                    ) : (
                        <>
                            {task.total_pending_submission_count <= 0 && (
                                <Text
                                    scale="caption400"
                                    color="secondary"
                                    style={{
                                        marginRight: 4,
                                        marginLeft: 4,
                                        textDecorationColor: theme.colors.text.secondary,
                                    }}
                                    textAlign="center"
                                    decoration="underline"
                                >
                                    {newT('card.submission.noSubmissionIndicator', 'No submissions to approve')}
                                </Text>
                            )}
                            {task.total_pending_submission_count > 0 &&
                                task.latest_submission_photo.resource_type === 'image' && (
                                    <img
                                        src={buildCloudinaryUrl(task.latest_submission_photo)}
                                        className="task-image"
                                    />
                                )}
                            {task.total_pending_submission_count > 0 &&
                                task.latest_submission_photo.resource_type === 'video' && (
                                    <video className="task-image">
                                        <source src={buildCloudinaryUrl(task.latest_submission_photo)} />
                                    </video>
                                )}
                        </>
                    )}
                </div>
            </a>
            <Text scale="bodyS500" color="primary">
                {title}
            </Text>
        </TaskBlockContainer>
    )
}

interface SubmissionsSummaryCardProps {
    campaign: SubmissionSummaryState
}

export const SubmissionsSummaryCard: React.FC<SubmissionsSummaryCardProps> = ({ campaign }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const canEditLiveCampaign = usePermission(CampaignManagement.LiveCampaignEditBriefs)
    const isInternalUser = useSelector(selectIsInternalUser)
    const [editingSubmissionStatus, setEditingSubmissionStatus] = useState<boolean>(false)
    const submissionStatusOptions = getSubmissionStatusOptions(newT)
    const teamTypeLabels = getTeamTypeOptions(newT)
    const status = submissionStatusOptions[campaign.submission_status].label
    const { brand } = campaign
    const statusColor =
        campaign.status === CAMPAIGN_STATUS_ENUM.CLOSED
            ? 'placeholder'
            : campaign.submission_status === CAMPAIGN_SUBMISSION_STATUS_ENUM.OPEN
              ? 'positive'
              : campaign.submission_status === CAMPAIGN_SUBMISSION_STATUS_ENUM.FILLING_FAST
                ? 'warning'
                : 'error'
    const registrationPendingCount = campaign.total_pending_registration_result_count
        ? campaign.total_pending_registration_result_count
        : 0

    const handleEditSubmissionStatus = () => setEditingSubmissionStatus(true)
    const handleSetSubmissionStatus = (value: number) => {
        dispatch(
            setCampaignSubmissionStatus({
                uuid: campaign.uuid,
                submissionStatus: value,
            }),
        )
        setEditingSubmissionStatus(false)
    }

    brand.logo_image = brand.logo_image || { image: '' }
    const dayLeft =
        campaign.overall_time_window && campaign.overall_time_window.end
            ? daysToDate(campaign.overall_time_window.end, campaign.country)
            : undefined

    return (
        <SummaryCardContainer campaignStatus={campaign.status} campaignSubmissionStatus={campaign.submission_status}>
            <div className="card-content">
                <div className="task-block-layout">
                    <div className="task-block-content">
                        <img className="brand-image" src={brand.logo_image.image} />
                        <div className="brand-block">
                            <Text scale="caption500" color="primary">
                                {campaign.title}
                            </Text>
                            <BrandNameContainer>
                                <Text scale="bodyS700" color="primary">
                                    {brand.name}
                                </Text>

                                {isInternalUser && !!brand.team?.type && (
                                    <TeamTypeTag label={teamTypeLabels[brand.team.type].title} />
                                )}
                            </BrandNameContainer>
                        </div>
                    </div>

                    {campaign.status !== CAMPAIGN_STATUS_ENUM.CLOSED && campaign.slot_number && (
                        <Progress
                            completedSlotCount={campaign.total_finished_submission_count}
                            totalSlotCount={campaign.slot_number}
                        />
                    )}
                    {typeof dayLeft === 'number' && (
                        <div className="day-left-block">
                            <Text scale="bodyS400" color="secondary">
                                {campaign.status === CAMPAIGN_STATUS_ENUM.CLOSED
                                    ? newT('statusLabel.campaignEnded')
                                    : dayLeft > 0
                                      ? newT('statusLabel.numberOfDaysLeft', { count: dayLeft })
                                      : dayLeft == 0
                                        ? newT('statusLabel.lessThanOneDay')
                                        : newT('statusLabel.campaignEnded')}
                            </Text>
                        </div>
                    )}

                    <div className="task-list">
                        {campaign.registration_enable ? (
                            <TaskBlock
                                campaignUuid={campaign.uuid}
                                task={campaign.tasks[0]}
                                viewType={TASK_VIEW_ENUM.REGISTRATION}
                                pendingCount={registrationPendingCount}
                                isCompleted={Boolean(campaign.registration_completed)}
                            />
                        ) : null}
                        <TaskBlock
                            campaignUuid={campaign.uuid}
                            task={campaign.tasks[0]}
                            viewType={TASK_VIEW_ENUM.BATCH_SUBMISSIONS}
                        />
                        {campaign.tasks.map((task: TaskSummary) => {
                            return (
                                <TaskBlock
                                    key={task.uuid}
                                    campaignUuid={campaign.uuid}
                                    task={task}
                                    viewType={TASK_VIEW_ENUM.TASKS}
                                    pendingCount={task.total_eligible_pending_submissions_count}
                                    isCompleted={Boolean(task.has_fulfilled_slot)}
                                />
                            )
                        })}
                        {/* empty box for justifyContent space evenly */}
                        <div className="empty-box"></div>
                    </div>
                    <ButtonList campaign={campaign} customLayOutStyle={summaryInnerCardButtonsStyle} tooltipId="mweb" />
                </div>
                <div className="button-block-container">
                    <ButtonList campaign={campaign} tooltipId="default" />
                </div>
            </div>
            <div className="submission-status-container">
                {editingSubmissionStatus ? (
                    <ClickAwayListener onClickAway={() => setEditingSubmissionStatus(false)}>
                        <Dropdown
                            width={425}
                            value={submissionStatusOptions[campaign.submission_status].value}
                            onChange={value => handleSetSubmissionStatus(value as number)}
                            open
                        >
                            {submissionStatusOptions.map((item, index) => (
                                <MenuItem.Text
                                    key={`submission-status-dropdown-${index}`}
                                    text={item.label}
                                    value={item.value}
                                    tabIndex={index}
                                />
                            ))}
                        </Dropdown>
                    </ClickAwayListener>
                ) : (
                    <>
                        <Text scale="bodyS700" color={statusColor} className="submission-status-text">
                            {campaign.status === CAMPAIGN_STATUS_ENUM.CLOSED
                                ? newT('statusLabel.submissionClosed', 'Submissions Closed')
                                : status}
                        </Text>
                        {!campaign.auto_update_submission_status &&
                            canEditLiveCampaign &&
                            campaign.status !== CAMPAIGN_STATUS_ENUM.CLOSED && (
                                <IconButton
                                    type="button"
                                    onClick={handleEditSubmissionStatus}
                                    buttonType="tertiary"
                                    size="s"
                                    className="submission-status-button"
                                >
                                    <Icon color={statusColor} size="16px" name="pencilSimple" />
                                </IconButton>
                            )}
                    </>
                )}
            </div>
        </SummaryCardContainer>
    )
}

export default SubmissionsSummaryCard

const BrandNameContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[200]};
`
